import React, {useState} from "react";
import Button from "@amzn/meridian/button";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Select, {SelectOption} from "@amzn/meridian/select";
import Input from "@amzn/meridian/input";
import Modal, {ModalFooter} from "@amzn/meridian/modal";
import Icon from "@amzn/meridian/icon";
import closeTokens from "@amzn/meridian-tokens/base/icon/close-large";
import {colorGray700} from "@amzn/meridian-tokens/base/color";
import {FormattedMessage, injectIntl} from "react-intl";
import {createEWasteContainer as createContainerApi} from "../../commons/api/CreateEWasteContainer";
import {WTN_MSG} from "../../commons/constants/WtnMessageIds";
import {
    getWasteCodesForLocale,
    CONTAINER_TYPES,
    WEIGHT_UNITS,
    INITIAL_CREATE_FORM
} from "../../commons/constants/EWasteConfig";

/**
 * Create-container Modal extracted from ContainerView.
 * Owns its own form state. Notifies parent through callbacks:
 *   onCreated()  — fired when the API call succeeds, parent should refetch
 *   onCancel()   — fired when the user dismisses the modal
 *   onError(msg) — fired on API failure so the parent can surface an alert
 */
const CreateContainerModal = ({open, siteName, intl, onCreated, onCancel, onError}) => {
    const [createForm, setCreateForm] = useState(INITIAL_CREATE_FORM);
    const [creating, setCreating] = useState(false);

    if (!open) return null;

    const wasteCodes = getWasteCodesForLocale(intl.locale);

    const handleCancel = () => {
        if (creating) return;
        setCreateForm(INITIAL_CREATE_FORM);
        onCancel && onCancel();
    };

    const confirmCreate = async () => {
        setCreating(true);
        const locale = (intl.locale || "en-GB").replace(/-/g, "_");
        const result = await createContainerApi(
            siteName,
            createForm.wasteProfileId,
            createForm.containerTypeId,
            {weight: Number(createForm.maxWeightValue), unit: createForm.maxWeightUnit},
            locale
        );
        setCreating(false);
        if (result.success) {
            setCreateForm(INITIAL_CREATE_FORM);
            onCreated && onCreated(result);
        } else {
            const msg = intl.formatMessage({
                id: WTN_MSG.CONTAINER.ERROR.CREATE_FAILED,
                defaultMessage: "Failed to create container."
            });
            onError && onError(msg);
        }
    };

    const isSaveDisabled = creating
        || !createForm.wasteProfileId
        || !createForm.containerTypeId
        || !createForm.maxWeightValue
        || !createForm.maxWeightUnit
        || !(Number(createForm.maxWeightValue) > 0);

    return (
        <Modal
            open={open}
            onClose={handleCancel}
            describe="Create container"
            width="500px"
            scrollContainer="modal"
        >
            {/* Header */}
            <Row
                alignmentHorizontal="justify"
                alignmentVertical="center"
                spacingInset="medium large"
                backgroundColor={colorGray700}
            >
                <Text type="h200" color="inverted">
                    <FormattedMessage id={WTN_MSG.CONTAINER.CREATE.TITLE} defaultMessage="Create container" />
                </Text>
                <Button type="icon" onClick={handleCancel}>
                    <Icon tokens={closeTokens}>
                        <FormattedMessage id={WTN_MSG.CONTAINER.CREATE.CANCEL} defaultMessage="Cancel" />
                    </Icon>
                </Button>
            </Row>

            <Column spacingInset="large" spacing="medium">
                <Column spacing="xsmall">
                    <Text type="b200" color="secondary">
                        <FormattedMessage id={WTN_MSG.CONTAINER.CREATE.WASTE_PROFILE_CODE} defaultMessage="Waste Code" />
                    </Text>
                    <Select
                        value={createForm.wasteProfileId}
                        onChange={(value) => setCreateForm((prev) => ({...prev, wasteProfileId: value}))}
                        size="small"
                        width="100%"
                    >
                        {wasteCodes.map((c) => (
                            <SelectOption key={c.value} value={c.value} label={c.label} />
                        ))}
                    </Select>
                </Column>
                <Column spacing="xsmall">
                    <Text type="b200" color="secondary">
                        <FormattedMessage id={WTN_MSG.CONTAINER.CREATE.CONTAINER_TYPE} defaultMessage="Container Type" />
                    </Text>
                    <Select
                        value={createForm.containerTypeId}
                        onChange={(value) => setCreateForm((prev) => ({...prev, containerTypeId: value}))}
                        size="small"
                        width="100%"
                    >
                        {CONTAINER_TYPES.map((t) => (
                            <SelectOption key={t.value} value={t.value} label={t.label} />
                        ))}
                    </Select>
                </Column>
                <Column spacing="xsmall">
                    <Text type="b200" color="secondary">
                        <FormattedMessage id={WTN_MSG.CONTAINER.CREATE.MAX_WEIGHT} defaultMessage="Max Weight" />
                    </Text>
                    <Row spacing="small" widths={["fill", "120px"]}>
                        <Input
                            type="number"
                            value={createForm.maxWeightValue}
                            onChange={(value) => setCreateForm((prev) => ({...prev, maxWeightValue: value}))}
                            size="small"
                            width="100%"
                        />
                        <Select
                            value={createForm.maxWeightUnit}
                            onChange={(value) => setCreateForm((prev) => ({...prev, maxWeightUnit: value}))}
                            size="small"
                            width="100%"
                        >
                            {WEIGHT_UNITS.map((u) => (
                                <SelectOption key={u} value={u} label={u} />
                            ))}
                        </Select>
                    </Row>
                </Column>
            </Column>

            <ModalFooter>
                <Row alignmentHorizontal="end" spacing="small">
                    <Button
                        type="secondary"
                        size="small"
                        disabled={creating}
                        onClick={handleCancel}
                    >
                        <FormattedMessage id={WTN_MSG.CONTAINER.CREATE.CANCEL} defaultMessage="Cancel" />
                    </Button>
                    <Button
                        type="primary"
                        size="small"
                        disabled={isSaveDisabled}
                        onClick={confirmCreate}
                    >
                        <FormattedMessage id={WTN_MSG.CONTAINER.CREATE.SAVE} defaultMessage="Save" />
                    </Button>
                </Row>
            </ModalFooter>
        </Modal>
    );
};

export default injectIntl(CreateContainerModal);



// WEBPACK FOOTER //
// ./src/components/wtn/CreateContainerModal.js